import { Cloudinary } from "@cloudinary/url-gen"
import { blur } from "@cloudinary/url-gen/actions/effect"
import { crop, Resize } from "@cloudinary/url-gen/actions/resize"
import {
  filterStreamVariants,
  getNowPlayingFromContent,
} from "src/store/PlayerStore"
import { create } from "zustand"

import {
  ArtistCollectionPageQuery,
  AudioDetailPageQuery,
  EditorialPlaylistPageQuery,
  EpochPageQuery,
  GenrePageQuery,
  GroupPageQuery,
  MaxResolution,
  PartnerPageQuery,
  PictureType,
  PublicationLevel,
  VideoDetailPageQuery,
  VideoType,
  WorkPageQuery,
} from "../../generated/graphql"
import { appStore } from "../../store/AppStore"
import { createContext } from "../../store/Store"
import { ccFactor, CloudinaryConf, s } from "../../utilities/common"
import { dimensions } from "../../utilities/constants"

export const { Provider, useStore: useDetailStore } =
  createContext<DetailState>()

const cld = new Cloudinary(CloudinaryConf)

export interface DetailPage {
  type: string
  id: string
  backgroundImage?: string
  albumCover?: string
  icons: string[]
  title: string
  subtitle?: string
  productionDate?: string
  videoType?: VideoType
  previewButton: boolean
  publicationLevel?: PublicationLevel
  links?: { type: string; id: string; title: string }[]
}

export type DetailpageData =
  | VideoDetailPageNode
  | AudioDetailPageNode
  | ArtistDetailPageNode
  | GroupDetailPageNode
  | PartnerDetailPageNode
  | EpochDetailPageNode
  | GenreDetailPageNode
  | WorkDetailPageNode
  | EditorialPlaylistPageNode

type Favorites = {
  content?: boolean
  vodConcert?: string[]
}

type SubscribeModal = { open: boolean; returnFocus?: string }

interface DetailState {
  content?: DetailpageData
  setContent: <T extends DetailpageData>(data?: T) => void
  page: DetailPage | undefined
  infoOpen: boolean
  setInfoOpen: (infoOpen: boolean) => void
  favorites: Favorites
  setFavorites: (favorites: Favorites) => void
  subscribeModal: SubscribeModal
  setSubscribeModal: (subscribeModal: SubscribeModal) => void
}

export type VideoDetailPageNode = Extract<
  VideoDetailPageQuery["node"],
  { __typename: "Video" | "VodConcert" | "LiveConcert" }
>

export type AudioDetailPageNode = Extract<
  AudioDetailPageQuery["album"],
  { __typename: "Album" }
>
export type ArtistDetailPageNode = Extract<
  ArtistCollectionPageQuery["artist"],
  { __typename: "Artist" }
>
export type GroupDetailPageNode = Extract<
  GroupPageQuery["group"],
  { __typename: "Group" }
>
export type PartnerDetailPageNode = Extract<
  PartnerPageQuery["partner"],
  { __typename: "Partner" }
>
export type EpochDetailPageNode = Extract<
  EpochPageQuery["epoch"],
  { __typename: "Epoch" }
>
export type GenreDetailPageNode = Extract<
  GenrePageQuery["genre"],
  { __typename: "Genre" }
>
export type WorkDetailPageNode = Extract<
  WorkPageQuery["work"],
  { __typename: "Work" }
>
export type EditorialPlaylistPageNode = Extract<
  EditorialPlaylistPageQuery["editorialPlaylist"],
  { __typename: "EditorialPlaylist" }
>

export type DetailPageTypes =
  | VideoDetailPageNode["__typename"]
  | AudioDetailPageNode["__typename"]
  | ArtistDetailPageNode["__typename"]
  | GroupDetailPageNode["__typename"]
  | PartnerDetailPageNode["__typename"]
  | EpochDetailPageNode["__typename"]

const backgroundImage = (data: DetailpageData) => {
  if (data.__typename === "EditorialPlaylist") return undefined
  if (
    data.__typename === "Video" ||
    data.__typename === "VodConcert" ||
    data.__typename === "LiveConcert"
  ) {
    const imageUrl = data.pictures.find(
      (p) => p.type === PictureType.Teaser,
    )?.url
    if (!imageUrl) return undefined
    return cld
      .image(imageUrl)
      .resize(Resize.scale().width(ccFactor(dimensions.width)))
      .format("jpg")
      .setDeliveryType("fetch")
      .toURL()
  }
  if (data.__typename === "Album") {
    const imageUrl = data?.pictures.find(
      (p) => p.type === PictureType.Cover,
    )?.url
    if (!imageUrl) return undefined
    return cld
      .image(imageUrl)
      .resize(Resize.scale().width(ccFactor(dimensions.width)))
      .resize(
        crop()
          .width(ccFactor(dimensions.width))
          .height(ccFactor(dimensions.height)),
      )
      .effect(blur().strength(2000))
      .format("jpg")
      .setDeliveryType("fetch")
      .toURL()
  }
  const imageUrl =
    "pictures" in data &&
    data.pictures.find((p) => p.type === PictureType.Teaser)?.url
  if (!imageUrl) return undefined
  return cld
    .image(imageUrl)
    .resize(
      Resize.scale().width(
        data.__typename === "Partner" ||
          data.__typename === "Group" ||
          data.__typename === "Epoch" ||
          data.__typename === "Artist" ||
          data.__typename === "Genre"
          ? s(ccFactor(1242), true)
          : ccFactor(dimensions.width),
      ),
    )
    .format("jpg")
    .setDeliveryType("fetch")
    .toURL()
}

const albumCover = (data: DetailpageData) => {
  if (data.__typename === "Album" || data.__typename === "EditorialPlaylist") {
    const imageUrl = data?.pictures.find(
      (p) => p.type === PictureType.Cover,
    )?.url
    if (!imageUrl) return undefined
    return cld
      .image(imageUrl)
      .resize(
        Resize.scale().width(
          s(ccFactor(data.__typename === "Album" ? 320 : 640), true),
        ),
      )
      .format("jpg")
      .setDeliveryType("fetch")
      .toURL()
  }
  return undefined
}

const icons = (data: DetailpageData): string[] => {
  const result = []
  if (data.__typename === "Video" || data.__typename === "VodConcert") {
    if (data.maxResolution === MaxResolution.Resolution_4K) result.push("4k")
    if (data.isAtmos) result.push("atmos")
  } else if (data.__typename === "Album") {
    if (data.atmosUpc) result.push("atmos")
  }
  return result
}

const previewButton = (data: DetailpageData) =>
  (data.__typename === "Video" ||
    data.__typename === "VodConcert" ||
    data.__typename === "LiveConcert") &&
  !!filterStreamVariants(data.trailerStreamVariants)?.url

export const getPageFromContent = async (content?: DetailpageData) => {
  let publicationLevel: PublicationLevel | undefined
  if (
    content &&
    (content.__typename === "Video" ||
      content.__typename === "VodConcert" ||
      content.__typename === "LiveConcert" ||
      content.__typename === "EditorialPlaylist" ||
      content.__typename === "Album")
  ) {
    const nowPlaying = await getNowPlayingFromContent({
      content,
      accessToken: appStore.getState().accessTokenState,
      skipUrlResolve: true,
    })
    publicationLevel = nowPlaying?.publicationLevel
  }
  return content
    ? {
        type: content.__typename,
        id: content.id,
        videoType: content.__typename === "Video" ? content.type : undefined,
        backgroundImage: backgroundImage(content),
        albumCover: albumCover(content),
        icons: icons(content),
        title:
          content.__typename !== "Artist" &&
          content.__typename !== "Group" &&
          content.__typename !== "Partner" &&
          content.__typename !== "EditorialPlaylist"
            ? content.title
            : content.__typename === "EditorialPlaylist"
              ? (content.metaTitle ?? content.title)
              : content.name,
        subtitle:
          content.__typename === "Video" ||
          content.__typename === "VodConcert" ||
          content.__typename === "LiveConcert" ||
          content.__typename === "Epoch"
            ? content.subtitle
            : content.__typename === "Partner" || content.__typename === "Group"
              ? content.typeDisplayName
              : content.__typename === "Artist"
                ? content.roles.map((r) => r.displayName).join(", ")
                : content.__typename === "Album"
                  ? content.artistAndGroupDisplayInfo
                  : content.__typename === "EditorialPlaylist"
                    ? content.metaDescription
                    : content.__typename === "Work"
                      ? content.composers.map((c) => c.name).join(", ")
                      : undefined,
        productionDate:
          content.__typename === "LiveConcert" ? content.startTime : undefined,
        previewButton: previewButton(content),
        publicationLevel,
        links:
          content.__typename === "VodConcert" ||
          content.__typename === "Album" ||
          content.__typename === "Video"
            ? [
                ...content.genresByRelevance.slice(0, 2),
                ...content.epochsByRelevance.slice(0, 2),
              ].map((item) => ({
                type: item.__typename,
                id: item.id,
                title: item.title,
              }))
            : content.__typename === "Artist" && content.epoch
              ? [
                  {
                    type: content.epoch.__typename,
                    id: content.epoch.id,
                    title: content.epoch.title,
                  },
                ]
              : undefined,
      }
    : undefined
}

export const createStore = () =>
  create<DetailState>((set) => ({
    content: undefined,
    setContent: async (content?: DetailpageData) => {
      set({
        content: content,
        page: await getPageFromContent(content),
      })
    },
    page: undefined,
    infoOpen: false,
    setInfoOpen: (infoOpen: boolean) => set({ infoOpen }),
    favorites: {},
    setFavorites: (favorites: Favorites) => set({ favorites }),
    subscribeModal: { open: false, returnFocus: undefined },
    setSubscribeModal: (subscribeModal) => set({ subscribeModal }),
  }))
